import type { Action } from 'redux';
import {
  call,
  put,
  takeLatest,
  fork,
  select,
} from 'redux-saga/effects';
import isIOSDevice from '@/utils/check/is-ios';

import api from '@/api/payment';
import {
  ERROR_PASS_REQUEST,
  ERROR_UPDATE_REQUEST,
} from '@/action-types/error';
import {
  GET_PACKAGES_REQUEST,
  GET_PACKAGES_SUCCESS,
  MAKE_PAYMENT_REQUEST,
  SET_PAYMENT_ERROR,
  APPLY_PROMOCODE,
  APPLY_PROMOCODE_SUCCESS,
} from '@/action-types/payment';
import { ANALYTICS_SEND_EVENT } from '@/action-types/analytics';

export function* getPackages() {
  try {
    const response = yield call(api.getPackages);

    yield put({ type: GET_PACKAGES_SUCCESS, data: response.data.getPackages?.data || [] });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'getPackages'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  }
}

export function* applyPromocode(params: Action & {
  promocode: string;
}) {
  try {
    const response = yield call(api.getPromocodes, params.promocode);
    const promocode = response.data.getPromocodes?.data?.[0];

    yield put({
      type: APPLY_PROMOCODE_SUCCESS,
      promocode,
      errorMessage: response.errors?.[0]?.extensions?.detail,
    });

    if (!promocode || response.errors?.length > 0) {
      yield put({
        type: ANALYTICS_SEND_EVENT,
        eventName: 'error_promo_code',
      });
    } else {
      yield put({
        type: ANALYTICS_SEND_EVENT,
        eventName: 'apply_promo-code',
        data: { promocode: promocode?.promo_code ?? '' },
      });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'applyPromocode'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  }
}

export function* continuePayment(params: {
  paymentId: number;
}) {
  try {
    const response = yield call(api.continuePayment, params.paymentId);
    const responseUrl = response.data.continuePayment.orderUrl;

    if (responseUrl) {
      if (isIOSDevice()) window.location = responseUrl;
      else window.open(responseUrl, '_blank');
    } else {
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: { errorText: 'Can\'t process your request.' } });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'continuePayment'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  }
}

export function* makePaymentRequest(params: Action & {
  priceId: number;
}) {
  try {
    const { payment: { promocode } } = yield select();
    const response = yield call(api.makePayment, params.priceId, promocode?.promo_code);

    if (response.data?.makePayment?.id) {
      yield fork(continuePayment, {
        paymentId: response.data.makePayment.id,
      });
    } else if (response.errors?.length > 0) {
      if (response.errors[0].extensions.status === 400) {
        yield put({
          type: SET_PAYMENT_ERROR,
          errorMessage: response.errors[0].extensions.detail,
        });
      } else {
        yield put(
          {
            type: ERROR_UPDATE_REQUEST,
            isActive: true,
            errorData: { errorText: response.errors[0].extensions.detail },
          },
        );
      }
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'makePaymentRequest'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  }
}

export default function* payment() {
  yield takeLatest(GET_PACKAGES_REQUEST, getPackages);
  yield takeLatest(APPLY_PROMOCODE, applyPromocode);
  yield takeLatest(MAKE_PAYMENT_REQUEST, makePaymentRequest);
}
